import {TLevelObject} from "../../config/types";
import {finalize} from "../core/FloatUtils.js";
import {Vec2} from "../core/Vec2.js";
import {
  SimulationEvent,
  SimulationEventType,
} from "../manager/SimulationManager.js";
import {Player} from "../player/Player.js";
import GameObject from "./GameObject.js";

/**
 * Represents a collectible shard in the game world.
 */
class Shard extends GameObject {
  public readonly position: Vec2;
  public readonly scale: Vec2;
  public readonly radius: number;
  public readonly rotation: number;
  public readonly id: number;

  constructor(
    position: Vec2,
    scale: Vec2,
    radius: number,
    rotation = 0,
    id: number,
  ) {
    super();
    this.position = position;
    this.scale = scale;
    this.radius = radius;
    this.rotation = rotation;
    this.id = id;
  }

  static create(obj: TLevelObject, id: number): Shard {
    const position = new Vec2(obj.position[0], obj.position[1]);
    const scale = new Vec2(obj.scale[0], obj.scale[1]);
    const radius = Math.min(obj.scale[0], obj.scale[1]) * 0.5;
    return new Shard(position, scale, radius, obj.rotation, id);
  }

  static calculateTimeOfImpact(
    previousPosition: Vec2,
    currentPosition: Vec2,
    shardPosition: Vec2,
    distanceOfCollision: number,
  ): number | null {
    const a = previousPosition.x - shardPosition.x;
    const b = currentPosition.x - previousPosition.x;
    const c = previousPosition.y - shardPosition.y;
    const d = currentPosition.y - previousPosition.y;

    const A = b * b + d * d;
    const B = 2 * (a * b + c * d);
    const C = a * a + c * c - distanceOfCollision * distanceOfCollision;

    const discriminant = B * B - 4 * A * C;
    if (discriminant < 0) return null;

    const sqrtDisc = Math.sqrt(discriminant);
    let t1 = (-B - sqrtDisc) / (2 * A);
    let t2 = (-B + sqrtDisc) / (2 * A);

    if (t1 > t2) [t1, t2] = [t2, t1];
    if (0 <= t1 && t1 <= 1) return finalize(t1);
    if (0 <= t2 && t2 <= 1) return finalize(t2);

    return null;
  }

  /**
   * Checks for a collision and returns a `PlayerHitShard` event if one occurred.
   */
  public process(player: Player): SimulationEvent[] {
    if (player.collectedShards.has(this.id)) {
      return [];
    }

    const touchDistance = player.radius + this.radius;
    const distance = player.currentPosition.distanceTo(this.position);

    if (distance <= touchDistance) {
      const timeOfImpact = Shard.calculateTimeOfImpact(
        player.previousPosition,
        player.currentPosition,
        this.position,
        touchDistance,
      );

      if (timeOfImpact !== null) {
        return [
          {
            type: SimulationEventType.PlayerHitShard,
            player,
            shardId: this.id,
            timeOfImpact: timeOfImpact,
          },
        ];
      }
    }

    return [];
  }
}

export default Shard;
